import { memo } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeftIcon, CheckBadgeIcon, XMarkIcon, TrashIcon } from '@heroicons/react/24/outline'

/**
 * SightingDetailHeader - Header with navigation and moderation actions
 * 
 * @param {boolean} isEditMode - Whether in edit mode or create mode
 * @param {Object} sighting - Sighting data (null for create mode)
 * @param {Function} onApprove - Callback for approve action
 * @param {Function} onReject - Callback for reject action  
 * @param {Function} onDelete - Callback for delete action
 * @param {boolean} loading - Loading state
 * 
 * @idempotent - Pure presentation component with React.memo
 * @atomic - Single responsibility: header with actions (<150 lines)
 */
const SightingDetailHeader = memo(function SightingDetailHeader({
  isEditMode,
  sighting,
  onApprove,
  onReject,
  onDelete,
  loading
}) {
  const navigate = useNavigate()
  
  const getModerationStatus = () => {
    if (!sighting) return null
    
    const status = sighting.moderationStatus
    if (status === 'approved') {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
          <CheckBadgeIcon className="h-4 w-4 mr-1" />
          Approved
        </span>
      )
    } else if (status === 'rejected') {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
          <XMarkIcon className="h-4 w-4 mr-1" />
          Rejected
        </span>
      )
    } else {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800">
          Pending Review
        </span>
      )
    }
  }

  return (
    <div className="mb-6">
      <button
        onClick={() => navigate('/admin/sightings')}
        className="flex items-center text-gray-600 hover:text-gray-900 mb-4 transition-colors"
      >
        <ArrowLeftIcon className="h-5 w-5 mr-2" />
        Back to Sightings
      </button>
      
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            {isEditMode ? 'Sighting Details' : 'Create New Sighting'}
          </h1>
          <p className="text-gray-500 mt-1">
            {isEditMode ? 'Review and moderate sighting report' : 'Report a new vendor sighting'}
          </p>
        </div>
        
        {isEditMode && sighting && (
          <div className="flex items-center gap-3">
            {getModerationStatus()}
            
            <div className="flex gap-2">
              {sighting.moderationStatus !== 'approved' && (
                <button
                  onClick={onApprove}
                  disabled={loading}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Approve
                </button>
              )}
              
              {sighting.moderationStatus !== 'rejected' && (
                <button
                  onClick={onReject}
                  disabled={loading}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Reject
                </button>
              )}
              
              <button
                onClick={onDelete}
                disabled={loading}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <TrashIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
})

export default SightingDetailHeader
